import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const GyerekekContext = createContext({
  gyerekek: [],
  betoltve: false,
  gyerekekFrissitese: async () => {},
});

// Hány lecke/küldetés van összesen az egyes korosztályi modulokban — a
// "NetOkos Hős" jelvényhez kell, hogy tudjuk, mikor van kész a teljes modul.
const MODUL_LECKESZAM = { '6-8': 8, '9-11': 10, '12-14': 10 };

// Egyetlen, közös helyen kiszámolt jelvény-lista egy adott gyerek adataiból.
// Ezt használja a ProfilOldal (a jelvény-rács kirajzolásához) ÉS a
// GyerekekContext (annak eldöntéséhez, hogy szerzett-e frissen jelvényt).
export function jelvenyekSzamitasa(gy) {
  if (!gy) return [];
  const halado = gy.halado || {};
  const halado68 = halado['6-8'] || [];
  const halado911 = halado['9-11'] || [];
  const halado1214 = halado['12-14'] || [];
  const teljesitettLeckek = halado68.length + halado911.length + halado1214.length;

  const sajatModulOsszes = MODUL_LECKESZAM[gy.koroszaly] || 0;
  const sajatModulTeljesitett = halado[gy.koroszaly]?.length || 0;
  const osszesLeckeKesz = sajatModulOsszes > 0 && sajatModulTeljesitett >= sajatModulOsszes;

  const teljesitesIdopontok = gy.teljesitesIdopontok || [];
  const naponkentiSzamlalo = {};
  teljesitesIdopontok.forEach(idoStr => {
    const nap = idoStr.slice(0, 10); // "YYYY-MM-DD"
    naponkentiSzamlalo[nap] = (naponkentiSzamlalo[nap] || 0) + 1;
  });
  const gyorsTanulo = Object.values(naponkentiSzamlalo).some(szam => szam >= 3);

  return [
    { ikon: '🌐', nev: 'Internet Bajnok', leiras: 'Teljesítsd az első leckédet', megszerzett: teljesitettLeckek >= 1 },
    { ikon: '🔒', nev: 'Jelszó Mester', leiras: 'Teljesíts 3 leckét', megszerzett: teljesitettLeckek >= 3 },
    { ikon: '🛡️', nev: 'Védelmező', leiras: 'Teljesíts 5 leckét', megszerzett: teljesitettLeckek >= 5 },
    { ikon: '🏆', nev: 'NetOkos Hős', leiras: 'Teljesítsd a korosztályod összes leckéjét', megszerzett: osszesLeckeKesz },
    { ikon: '🚀', nev: 'Gyors Tanuló', leiras: 'Teljesíts 3 leckét egy nap alatt', megszerzett: gyorsTanulo },
  ];
}

// Egyetlen, közös hely a gyerek-profilok (Firestore: felhasznalok/{uid}.gyerekek)
// betöltésére és frissítésére. Bárhol változik (Profil oldal, Temakork), az
// összes komponens (pl. Navbar) azonnal látja az új állapotot — nincs szükség
// oldal-frissítésre.
export function GyerekekProvider({ felhasznalo, children }) {
  const [gyerekek, setGyerekekAllapot] = useState([]);
  const [betoltve, setBetoltve] = useState(false);

  // Globális "új jelvény" állapot: { gyerekId, jelveny } vagy null. Bárhol az
  // appban (lecke közben, profilon, bárhol) amikor ez nem null, a JelvenyAblak
  // komponens megjeleníti a felugró ablakot.
  const [ujJelveny, setUjJelveny] = useState(null);

  useEffect(() => {
    if (!felhasznalo) {
      setGyerekekAllapot([]);
      setBetoltve(true);
      return;
    }
    setBetoltve(false);
    const betolt = async () => {
      const ref = doc(db, 'felhasznalok', felhasznalo.uid);
      const pillanatkep = await getDoc(ref);
      setGyerekekAllapot(pillanatkep.exists() ? (pillanatkep.data().gyerekek || []) : []);
      setBetoltve(true);
    };
    betolt();
  }, [felhasznalo]);

  // Frissíti a megosztott állapotot ÉS a Firestore-t egy lépésben — így
  // minden komponens, ami ezt a context-et használja, azonnal újrarenderel.
  const gyerekekFrissitese = async (ujLista) => {
    setGyerekekAllapot(ujLista);
    if (felhasznalo) {
      await setDoc(doc(db, 'felhasznalok', felhasznalo.uid), { gyerekek: ujLista }, { merge: true });
    }
  };

  // Csak a helyi (megosztott) állapotot frissíti, Firestore-írás nélkül —
  // olyankor jó, ha a Firestore-írás már megtörtént máshol (pl. extra
  // mezőkkel együtt), és csak a UI-t kell szinkronizálni.
  const gyerekekHelyiFrissitese = (ujLista) => {
    setGyerekekAllapot(ujLista);
  };

  // XP hozzáadása egy adott gyerekhez (pl. lecke/küldetés teljesítésekor).
  // A hívó felelőssége eldönteni, hogy még nem járt-e XP az adott
  // leckéért/küldetésért (pl. a `teljesitett` lista ellenőrzésével),
  // hogy ne adjon XP-t kétszer ugyanazért.
  const xpHozzaadasa = async (gyerekId, xpOsszeg) => {
    const ujLista = gyerekek.map(gy =>
      gy.id === gyerekId ? { ...gy, xp: (gy.xp || 0) + xpOsszeg } : gy
    );
    await gyerekekFrissitese(ujLista);
  };

  // KOMBINÁLT mentés: XP hozzáadása + haladás mentése + időbélyeg EGY,
  // atomi Firestore-írásban. Ezt kell hívni lecke/küldetés befejezésekor —
  // NE külön xpHozzaadasa + haladasMentese hívással, mert az két külön
  // írást indítana, amik mindketten a régi (elavult) állapotból számolnának,
  // és a második felülírná/eltüntetné az első által hozzáadott XP-t
  // (verseny­helyzet / "lost update" hiba — ez okozta, hogy az XP egy idő
  // után nem nőtt tovább).
  const leckeTeljesitese = async (gyerekId, modul, ujTeljesitettLista, xpOsszeg) => {
    const most = new Date().toISOString();
    let frissGyerek = null;
    const ujLista = gyerekek.map(gy => {
      if (gy.id !== gyerekId) return gy;
      frissGyerek = {
        ...gy,
        xp: (gy.xp || 0) + xpOsszeg,
        halado: { ...(gy.halado || {}), [modul]: ujTeljesitettLista },
        teljesitesIdopontok: [...(gy.teljesitesIdopontok || []), most],
      };
      return frissGyerek;
    });
    await gyerekekFrissitese(ujLista);

    if (frissGyerek) {
      const jelzettek = frissGyerek.jelzettJelvenyek || [];
      const ujakEmelt = jelvenyekSzamitasa(frissGyerek).find(j => j.megszerzett && !jelzettek.includes(j.nev));
      if (ujakEmelt) setUjJelveny({ gyerekId, jelveny: ujakEmelt });
    }
  };

  // Egy adott korosztályi modul (pl. '6-8', '9-11', '12-14') teljesített
  // leckéinek/küldetéseinek mentése a gyerek profiljába. A `gy.halado` mező
  // modulonként tárolja a teljesített ID-k listáját, pl.:
  // { '6-8': [1,2,3], '9-11': [1], '12-14': [] }
  // A `gy.teljesitesIdopontok` egy időbélyeg-lista, minden teljesítéskor
  // bővül eggyel — ebből számoljuk ki pl. a "Gyors Tanuló" jelvényt.
  // FIGYELEM: ha XP-t IS hozzá kell adni ugyanekkor, inkább a fenti
  // `leckeTeljesitese`-t használd — ez a függvény önmagában csak akkor
  // biztonságos, ha xp-módosítás nélkül, önállóan hívod.
  const haladasMentese = async (gyerekId, modul, ujTeljesitettLista) => {
    const most = new Date().toISOString();
    let frissGyerek = null;
    const ujLista = gyerekek.map(gy => {
      if (gy.id !== gyerekId) return gy;
      frissGyerek = {
        ...gy,
        halado: { ...(gy.halado || {}), [modul]: ujTeljesitettLista },
        teljesitesIdopontok: [...(gy.teljesitesIdopontok || []), most],
      };
      return frissGyerek;
    });
    await gyerekekFrissitese(ujLista);

    if (frissGyerek) {
      const jelzettek = frissGyerek.jelzettJelvenyek || [];
      const ujakEmelt = jelvenyekSzamitasa(frissGyerek).find(j => j.megszerzett && !jelzettek.includes(j.nev));
      if (ujakEmelt) setUjJelveny({ gyerekId, jelveny: ujakEmelt });
    }
  };

  // A jelvény-ablak bezárása: elmentjük, hogy erről a jelvényről már szóltunk
  // a gyereknek (jelzettJelvenyek), így soha többé nem ugrik fel újra.
  const jelvenyBezaras = async () => {
    if (!ujJelveny) return;
    const { gyerekId, jelveny } = ujJelveny;
    setUjJelveny(null);
    const celGyerek = gyerekek.find(gy => gy.id === gyerekId);
    const jelzettek = celGyerek?.jelzettJelvenyek || [];
    const ujLista = gyerekek.map(gy =>
      gy.id === gyerekId ? { ...gy, jelzettJelvenyek: [...jelzettek, jelveny.nev] } : gy
    );
    await gyerekekFrissitese(ujLista);
  };

  return (
    <GyerekekContext.Provider value={{
      gyerekek, betoltve, gyerekekFrissitese, gyerekekHelyiFrissitese, xpHozzaadasa,
      haladasMentese, leckeTeljesitese, ujJelveny, jelvenyBezaras,
    }}>
      {children}
    </GyerekekContext.Provider>
  );
}

export function useGyerekek() {
  return useContext(GyerekekContext);
}